import React, { ReactElement } from 'react'

import AssessmentIcon from '@mui/icons-material/Assessment'
import BookIcon from '@mui/icons-material/Book'
import HelpIcon from '@mui/icons-material/Help'
import HomeIcon from '@mui/icons-material/Home'
import LaunchIcon from '@mui/icons-material/Launch'
import MapIcon from '@mui/icons-material/Map'
import SearchIcon from '@mui/icons-material/Search'
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import ViewWeekIcon from '@mui/icons-material/ViewWeek'
import WidgetsIcon from '@mui/icons-material/Widgets'
import { ReactComponent as InProgress } from '../../shared/images/InProgress.svg'

import { Target } from '../../shared/buttons/Button'
import {
  AdminUserPermission,
  DegreeRequirementsPanelConfiguration
} from '../../../generated/graphql'
import { Auth0User, Roles } from '../../../types'
import { userHasPermission, userHasRole } from '../../../lib/authenticationUtils'

export type NavButton = {
  /** Accessible label for the button */
  ariaLabel: string
  /** Icon to use for the button */
  icon: ReactElement
  /**
   * Whether this is a redirect outside of the student app.
   * Also needs the route prop to redirect properly.
   */
  isExternalLink?: boolean
  /** Label to display for the button */
  label: string
  /** Optional click handler */
  onClick?: () => void
  /** Optional permissions to apply to the button.
   * If a list of permissions is provided, access is granted if the user
   * has at least one permission.  Access to all permissions is not required.
   */
  permissions?: AdminUserPermission | Array<AdminUserPermission>
  /** Optional route for the button */
  route?: string
  /** Optional target attribute for external links */
  target?: Target
  /** If we should add a mixpanel event onClick of the button */
  trackNavigation?: boolean
}

/**
 * Creates the url for scheduler monolith based on the current environment, unless
 * REACT_APP_SCHEDULER_MONOLITH_URI is populated.
 * @param environment - The current environment
 * @returns The fully qualified URL to Scheduler Monolith
 */
function getMonolithUrl(environment: string) {
  const schedulerMonolithUri = process.env.REACT_APP_SCHEDULER_MONOLITH_URI
  const monolithHost = process.env.REACT_APP_SCHEDULER_HOST_URI
  const useDevelop =
    (window.location.hostname.includes('.develop') ||
      window.location.hostname.includes('localhost')) &&
    !monolithHost?.includes('develop.')
  const schedulerMonolithDomain = useDevelop ? 'collegeschedulerapp' : 'collegescheduler'
  const schedulerMonolithHostUri = monolithHost || schedulerMonolithDomain
  const developSubdomain = useDevelop ? '.develop' : ''

  const url =
    schedulerMonolithUri ||
    `https://${environment}${developSubdomain}.${schedulerMonolithHostUri}.com`

  return url
}

/**
 * Creates a NavButton with the url for scheduler monolith and the ownerID in the querystring
 * @param environment - The current environment
 * @param userAppUserId - User's appUserId
 * @param userSub - User's Sub ID
 * @param scheduleLabel - The "Schedule" Label. Used for link's text.
 * @returns NavButton populated with the fully qualified URL to Scheduler Monolith with ownerId querystring
 */
function getScheduleLink(scheduleLabel: string, onSchedulerClick: () => void): NavButton {
  const scheduleLink = {
    ariaLabel: `Go to the ${scheduleLabel} app`,
    icon: <ViewWeekIcon />,
    isExternalLink: true,
    label: scheduleLabel,
    onClick: onSchedulerClick
  }

  return scheduleLink
}

function getOldAdminUrl(environment: string): string {
  const baseUrl = getMonolithUrl(environment)
  return `${baseUrl}/admin`
}

/**
 * Creates a NavButton with the url to Old Admin
 * @param environment - The current environment
 * @returns NavButton populated with with the fully qualified URL to Old Admin
 */
function getOldAdminLink(environment: string): NavButton {
  const oldAdminUrl = getOldAdminUrl(environment)
  return {
    ariaLabel: 'Go to the Scheduling Tools',
    icon: <LaunchIcon />,
    isExternalLink: true,
    label: 'Scheduling Tools',
    route: oldAdminUrl,
    target: Target.Blank,
    trackNavigation: true
  }
}

function getManageUsersLink(environment: string): NavButton {
  const oldAdminUrl = getOldAdminUrl(environment)
  return {
    ariaLabel: 'Go to Manage Users',
    icon: <LaunchIcon />,
    isExternalLink: true,
    label: 'Manage Users',
    route: oldAdminUrl,
    target: Target.Blank
  }
}

const dashboardLink: NavButton = {
  ariaLabel: 'Go to Dashboard',
  icon: <HomeIcon />,
  label: 'Dashboard',
  route: '/dashboard'
}
const getSearchLink = (courseLabel: string): NavButton => ({
  ariaLabel: `Go to ${courseLabel} Search`,
  icon: <SearchIcon />,
  label: `${courseLabel} Search`,
  route: '/search',
  trackNavigation: true
})
const planLink: NavButton = {
  ariaLabel: 'Go to Planner',
  icon: <MapIcon />,
  label: 'Plan',
  route: '/plans'
}
const courseHistoryLink: NavButton = {
  ariaLabel: 'Go to Course History',
  icon: <BookIcon />,
  label: 'Course History',
  route: '/course-history'
}
const templatesLink: NavButton = {
  ariaLabel: 'Go to Templates',
  icon: <WidgetsIcon />,
  label: 'Templates',
  route: '/templates',
  trackNavigation: true
}
const reportsLink: NavButton = {
  ariaLabel: 'Go to Reports',
  icon: <AssessmentIcon />,
  label: 'Reports',
  permissions: [
    AdminUserPermission.CourseDemandRawData,
    AdminUserPermission.StudentUsageRawData,
    AdminUserPermission.StudentUsageReports
  ],
  route: '/reports',
  trackNavigation: true
}
const requirementsLink: NavButton = {
  ariaLabel: 'Go to the Degree Requirements Page',
  label: 'Requirements',
  route: '/requirements',
  icon: <InProgress />
}
const resourcesLink: NavButton = {
  ariaLabel: 'Go to Resources and Help Page',
  label: 'Resources',
  route: '/resources',
  icon: <HelpIcon />,
  trackNavigation: true
}
const diagnosticsLink: NavButton = {
  ariaLabel: 'Go to Diagnostics',
  icon: <SettingsInputComponentIcon />,
  label: 'Diagnostics',
  permissions: AdminUserPermission.Diagnostics,
  route: '/diagnostics'
}

const adminUploaderLink: NavButton = {
  ariaLabel: 'Go to Admin User Uploader',
  icon: <CloudUploadIcon />,
  label: 'Create Users',
  route: '/users/upload-csv'
}

export function getStudentMainNav(
  courseLabel: string,
  scheduleLabel: string,
  onSchedulerClick: () => void,
  degreeRequirementsPanelConfiguration: DegreeRequirementsPanelConfiguration
) {
  const studentMainNav: Array<NavButton> = [
    planLink,
    getScheduleLink(scheduleLabel, onSchedulerClick)
  ]

  const { Disabled } = DegreeRequirementsPanelConfiguration
  if (degreeRequirementsPanelConfiguration !== Disabled) {
    studentMainNav.push(requirementsLink)
  }
  studentMainNav.push(courseHistoryLink)
  studentMainNav.push(getSearchLink(courseLabel))
  studentMainNav.push(resourcesLink)
  return studentMainNav
}

export function getAdminMainNav(user: Auth0User | undefined): Array<NavButton> {
  const adminMainNav = [dashboardLink, templatesLink]

  if (userHasPermission(user, reportsLink.permissions)) {
    adminMainNav.push(reportsLink)
  }

  return adminMainNav
}

export function getAdvisingNav(
  courseLabel: string,
  scheduleLabel: string,
  isStudent: boolean,
  onSchedulerClick: () => void,
  degreeRequirementsPanelConfiguration: DegreeRequirementsPanelConfiguration
) {
  const advisingNav: Array<NavButton> = []

  // If the advisor/admin has a dual student role, also show the student nav
  if (isStudent) {
    advisingNav.push(planLink)
    advisingNav.push(getScheduleLink(scheduleLabel, onSchedulerClick))
    const { Disabled } = DegreeRequirementsPanelConfiguration
    if (degreeRequirementsPanelConfiguration !== Disabled) {
      advisingNav.push(requirementsLink)
    }
    advisingNav.push(courseHistoryLink)
  }

  advisingNav.push(getSearchLink(courseLabel))
  advisingNav.push(resourcesLink)
  return advisingNav
}

export function getAdminOnlyNav(
  user: Auth0User | undefined,
  environment: string
): Array<NavButton> {
  const adminOnlyNav = []

  if (userHasPermission(user, diagnosticsLink.permissions)) {
    adminOnlyNav.push(diagnosticsLink)
  }

  if (userHasPermission(user, AdminUserPermission.ManageUsers)) {
    adminOnlyNav.push(getManageUsersLink(environment))
  }

  if (userHasRole(user, [Roles.civitasEmployee])) {
    adminOnlyNav.push(adminUploaderLink)
  }

  return adminOnlyNav
}

/**
 * Create list of links to external apps, such Advising and Scheduling Tools (Old Admin).
 * List of links will be target=_blank
 * @param environment - The current environment
 * @param isAdmin - Whether the current user is a non-student user
 * @param showAdvisingLink - Whether to show the Advising Link
 * @param advisingUrl - Advising URL configuration
 * @returns list of links to external apps.
 */
export function getExternalAppLinks(
  environment: string,
  isAdmin: boolean | undefined,
  showAdvisingLink: boolean,
  advisingUrl: string
) {
  const externalAppLinks: Array<NavButton> = []

  if (showAdvisingLink && advisingUrl) {
    externalAppLinks.push({
      ariaLabel: 'Go to the Advising App',
      icon: <LaunchIcon />,
      isExternalLink: true,
      label: 'Advising',
      route: advisingUrl,
      target: Target.Blank,
      trackNavigation: true
    })
  }

  if (isAdmin) {
    externalAppLinks.push(getOldAdminLink(environment))
  }
  return externalAppLinks
}
